/**
 * Recover from stale-chunk errors after a deploy.
 *
 * When a new frontend deploys, `index.html` references new lazy-chunk
 * filenames (hashed). A browser tab that was already open holds the OLD
 * index in memory, so navigating to a lazy route requests a chunk filename
 * the deploy has removed. The dynamic import then fails with
 * "Failed to fetch dynamically imported module" and the view goes blank.
 *
 * Vite fires a `vite:preloadError` event for exactly this case. We reload
 * once so the browser fetches the fresh `index.html` (served by the updated
 * service worker) with the correct chunk hashes. A short cooldown stored in
 * sessionStorage prevents an infinite reload loop if a reload doesn't fix it
 * (e.g. a genuinely missing chunk).
 */

const RELOAD_FLAG = 'andi:chunk-reloaded-at';
const RELOAD_COOLDOWN_MS = 10_000;

/** Messages browsers use when a dynamically imported module fails to load. */
const DYNAMIC_IMPORT_ERROR =
  /(Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed)/i;

export function isDynamicImportError(message: unknown): boolean {
  return typeof message === 'string' && DYNAMIC_IMPORT_ERROR.test(message);
}

function recentlyReloaded(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_FLAG) ?? '0');
    return Date.now() - last < RELOAD_COOLDOWN_MS;
  } catch {
    return false;
  }
}

function markReloaded(): void {
  try {
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    // sessionStorage unavailable (private mode, etc.) — best effort.
  }
}

/**
 * Install global handlers that reload once on a stale-chunk failure.
 *
 * @param reload Injectable reload function (defaults to window.location.reload)
 *   so tests can assert the behavior without navigating jsdom.
 * @returns An uninstall function that removes the listeners (used in tests).
 */
export function installChunkReloadHandler(
  reload: () => void = () => {
    window.location.reload();
  }
): () => void {
  const reloadOnce = () => {
    if (recentlyReloaded()) return; // avoid a reload loop
    markReloaded();
    reload();
  };

  // Vite's preload-error event for failed dynamic chunk imports.
  const onPreloadError = (event: Event) => {
    // Prevent Vite's default (rethrow) — we handle recovery via reload.
    event.preventDefault();
    reloadOnce();
  };

  // Native dynamic import()/React.lazy failures that surface as unhandled
  // promise rejections rather than vite:preloadError.
  const onRejection = (event: PromiseRejectionEvent) => {
    const reason: unknown = event.reason;
    const message =
      typeof reason === 'string' ? reason : reason instanceof Error ? reason.message : undefined;
    if (isDynamicImportError(message)) {
      reloadOnce();
    }
  };

  window.addEventListener('vite:preloadError', onPreloadError as EventListener);
  window.addEventListener('unhandledrejection', onRejection);

  return () => {
    window.removeEventListener('vite:preloadError', onPreloadError as EventListener);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}
